import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

interface NoRecordsFoundProps {
  itemName?: string;
  message?: string;

  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}

/*
 * The component's original design dimensions.
 *
 * Everything inside the component is positioned and sized relative
 * to these dimensions, preserving the same proportions as it scales.
 */
const BASE_WIDTH = 240;
const BASE_HEIGHT = 160;

export default function NoRecordsFound({
  itemName = "items",
  message,
  minWidth = 120,
  maxWidth = 360,
  minHeight = 80,
  maxHeight = 240,
}: NoRecordsFoundProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [availableSize, setAvailableSize] = useState({
    width: maxWidth,
    height: maxHeight,
  });

  const displayMessage = message ?? `No ${itemName} found`;

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const updateSize = () => {
      const parent = element.parentElement;

      if (!parent) {
        return;
      }

      const parentRect = parent.getBoundingClientRect();

      setAvailableSize({
        width: parentRect.width,
        height: parentRect.height,
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);

    if (element.parentElement) {
      resizeObserver.observe(element.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useMemo(() => {
    /*
     * First restrict the available space using the component's
     * minimum and maximum dimensions.
     */
    const boundedWidth = Math.min(
      maxWidth,
      Math.max(minWidth, availableSize.width)
    );

    const boundedHeight = Math.min(
      maxHeight,
      Math.max(minHeight, availableSize.height)
    );

    /*
     * Whichever dimension produces the smaller scale becomes
     * the limiting dimension.
     */
    const widthScale = boundedWidth / BASE_WIDTH;
    const heightScale = boundedHeight / BASE_HEIGHT;

    const scale = Math.min(widthScale, heightScale);

    return {
      scale,
      width: BASE_WIDTH * scale,
      height: BASE_HEIGHT * scale,
    };
  }, [
    availableSize,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
  ]);
  
  const { scale, width, height } = dimensions;

  return (
    <Box
        ref={containerRef}
        role="status"
        aria-label={displayMessage}
        sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "hidden",
        textAlign: "center",
        }}
    >
        <Box
        sx={{
            width,
            height,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            flexShrink: 0,
        }}
        >
        <Box
            sx={{
            position: "relative",
            width: 90 * scale,
            height: 90 * scale,
            mb: `${16 * scale}px`,
            color: "text.secondary",
            }}
        >
            <SearchIcon
            sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
            }}
            />

            <Box
            sx={{
                position: "absolute",
                top: 4 * scale,
                right: -4 * scale,
                width: 44 * scale,
                height: 44 * scale,
                borderRadius: "50%",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            >
            <CloseIcon
                sx={{
                width: 36 * scale,
                height: 36 * scale,
                color: "error.main",
                }}
            />
            </Box>
        </Box>

        <Typography
            component="p"
            sx={{
            fontSize: `${20 * scale}px`,
            lineHeight: 1.25,
            fontWeight: 600,
            color: "text.secondary",
            maxWidth: `${220 * scale}px`,
            overflowWrap: "break-word",
            }}
        >
            {displayMessage}
        </Typography>
        </Box>
    </Box>
    );
}